import { api } from 'app/area/case/features/search-external-provider/api';
import { debug } from 'app/infrastructure/log';
import { whenIdle } from 'app/infrastructure/redux';
import { actionCreators as draftActionCreators } from 'app/area/case/draft/actions';
import { getEntityContext, getCaseData } from 'app/infrastructure/ui/renderer/resolvers';
import { getById } from 'app/infrastructure/store/query';
import {
  RequestSearchExternalProvider,
  ReceiveSearchExternalProvider,
  FailSearchExternalProvider,
  ClearSearchExternalProviderResults
} from './action-types';
import { ApplicationState } from 'app/types-store';
import { selectEntityFieldValues } from 'app/area/case/features/search-external-provider/selectors';
import { ComponentProps, ConnectedComponentProps } from 'app/infrastructure/ui/types-uischema';
import { SearchExternalResultValue } from 'app/area/case/types-store';
import get from 'lodash/get';

const executeSearch = async (
  dispatch: any, 
  caseDraftId: number, 
  domainId: number, 
  searchConditions: { dataTag: string, value: string }[],
  properties: ComponentProps) => {
  
  debug('searchExternalProvider', 'Done saving.');
  debug('searchExternalProvider', 'Executing search..');
  try {
    const result = await api.searchExternalProvider(caseDraftId, domainId, searchConditions);
    dispatch(new ReceiveSearchExternalProvider(caseDraftId, domainId, result));
  } catch (error) {
    if (error && error.response && error.response.status === 400) {
      const responseError = error?.userMessages?.length ?
        error.userMessages.map(m => m.message).join('\n\n') :
        undefined;
      
      dispatch(
        new FailSearchExternalProvider(
          caseDraftId,
          domainId,
          responseError || (properties && properties.textInvalidSearchKeys) || error
        )
      );
    } else {
      dispatch(
        new FailSearchExternalProvider(caseDraftId, domainId, (properties && properties.textGeneralError) || error)
      );
    }
  }
  debug('searchExternalProvider', 'Done executing search..');
};

export const searchExternalProvider = (uiEngineProps: ConnectedComponentProps) => async (
  dispatch: any,
  getState: () => ApplicationState
) => {
  debug('searchExternalProvider', 'Saving..');
  
  const appState = getState();
  const caseState = get(appState, uiEngineProps.hostProps().statePath);
  const caseData = getCaseData(caseState, uiEngineProps);
  const resolveContext = getEntityContext(appState, uiEngineProps);
  const entity = getById(caseData, resolveContext.path);

  const caseDraftId = resolveContext.path[0].tblTrans;
  const domainId = uiEngineProps.componentDescriptor.id;
  const entityFieldValues = selectEntityFieldValues(entity);
  const properties = uiEngineProps.componentDescriptor.properties;
  const inputFields = properties.dataProviderInputFields as string;
  let searchConditions = [];
  if (inputFields) {
    const requiredFields = inputFields.split(',').map(r => r.trim());
    searchConditions = entityFieldValues.filter(v => requiredFields.some(r => v.dataTag === r));
  }

  if (searchConditions.length === 0) {
    return;
  }

  dispatch(new RequestSearchExternalProvider(caseDraftId, domainId, searchConditions));
  dispatch(draftActionCreators.requestImmediateSave());

  return await dispatch(
    whenIdle(async () =>
      await executeSearch(dispatch, caseDraftId, domainId, searchConditions, properties)
    )
  );
};

export const clearResults = () => new ClearSearchExternalProviderResults();

export const setValuesFromResult = (
  uiEngineProps: ConnectedComponentProps,
  result: SearchExternalResultValue[]
) => async (dispatch: any, getState: () => ApplicationState) => {
  const appState = getState();
  const resolveContext = getEntityContext(appState, uiEngineProps);

  for (const v of result.filter(r => !r.failedToMapCodeValue)) {
    const fieldValue = v.codeValue ?
      { value: v.codeValue.v, description: v.codeValue.d } :
      { value: v?.numberValue ?? v.value };
    
    dispatch(draftActionCreators.valueChanged(resolveContext.path, v.dataTag, [fieldValue]));
  }
};
